import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useClient = () => {
  const { client } = useAuth();
  return client;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication on mount
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
        
        if (userData.client) {
          // Fetch client config
          const configResponse = await fetch(`${API_URL}/api/client/config`, { credentials: 'include' });
          if (configResponse.ok) {
            const configData = await configResponse.json();
            setClient({ ...userData.client, ...configData });
            // Apply theme
            // applyTheme(configData);
          }
        } else {
          // Usuario admin sin cliente específico
          setClient(null);
        }
      } else if (response.status === 401) {
        console.log('🔍 AuthContext: Sesión expirada (401), limpiando estado...');
        setUser(null);
        setClient(null);
        // No navegar aquí - dejar que el middleware o el componente lo maneje
      } else {
        console.log('🔍 AuthContext: Error en autenticación, redirigiendo a login...');
        setUser(null);
        setClient(null);
      }
    } catch (error) {
      console.error('🔍 AuthContext: Error en verificación:', error);
      setUser(null);
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`${API_URL}/portal/login`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Login exitoso, verificar autenticación (y que la cookie se use en /api/me)
          await checkAuth();
          const role = data.user?.role ?? null;
          return { success: true, role };
        } else {
          return { success: false, error: data.error || 'Credenciales inválidas' };
        }
      } else {
        const errorText = await response.text();
        return { success: false, error: 'Credenciales inválidas' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login error' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/logout`, { 
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      setUser(null);
      setClient(null);
      // Usar navigate del router en lugar de window.location.href
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Aún así limpiar el estado local y redirigir
      setUser(null);
      setClient(null);
      navigate('/login');
    }
  };

  const applyTheme = (config) => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', config.primary_color || '#0b1020');
    root.style.setProperty('--secondary-color', config.secondary_color || '#3b82f6');
    root.style.setProperty('--logo-url', config.logo_url || '');
  };

  const value = {
    user,
    client,
    loading,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};